import { useEffect, useMemo, useState } from "react";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import type { CreateProviderStaffInput, ProviderMembershipRole, ProviderTeamMember, ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import {
  membershipRoleLabel,
  resolveProviderMembershipRole,
} from "../provider-workspace/provider-workspace-helpers";
import { useProviderTeam } from "./useProviderTeam";

const initialForm: CreateProviderStaffInput = {
  email: "",
  display_name: "",
  password: "",
  role: "front_desk",
  venue_ids: null,
};

type VenueScopeMode = "all" | "specific";

function getMemberMembershipRole(member: ProviderTeamMember): ProviderMembershipRole {
  return (
    member.membership_role ??
    (member.role === "provider_owner" ? "owner" : "front_desk")
  );
}

function sameVenueScope(left: string[] | null, right: string[] | null) {
  if (left === null || right === null) {
    return left === right;
  }

  if (left.length !== right.length) {
    return false;
  }

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

function getVenueScopeLabel(
  member: ProviderTeamMember,
  venues: ProviderVenue[],
  t: (key: string, values?: Record<string, string | number>) => string,
) {
  if (member.role === "provider_owner" || member.venue_ids === null) {
    return t("All venues");
  }

  if (member.venue_ids.length === 0) {
    return t("No venues assigned");
  }

  if (member.venue_ids.length === 1) {
    const venue = venues.find((item) => item.id === member.venue_ids?.[0]);
    return venue?.name ?? t("{count} venue", { count: 1 });
  }

  return t("{count} venues", { count: member.venue_ids.length });
}

function TeamVenueScopeEditor({
  disabled,
  member,
  onSave,
  updating,
  venues,
}: {
  disabled: boolean;
  member: ProviderTeamMember;
  onSave: (venueIds: string[] | null) => Promise<unknown>;
  updating: boolean;
  venues: ProviderVenue[];
}) {
  const { t } = useAppPreferences();
  const [mode, setMode] = useState<VenueScopeMode>(member.venue_ids === null ? "all" : "specific");
  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>(member.venue_ids ?? []);

  useEffect(() => {
    setMode(member.venue_ids === null ? "all" : "specific");
    setSelectedVenueIds(member.venue_ids ?? []);
  }, [member.venue_ids]);

  const nextVenueIds = mode === "all" ? null : selectedVenueIds;
  const hasChanges = !sameVenueScope(member.venue_ids, nextVenueIds);

  const toggleVenue = (venueId: string) => {
    setSelectedVenueIds((current) =>
      current.includes(venueId)
        ? current.filter((id) => id !== venueId)
        : [...current, venueId],
    );
  };

  return (
    <div className="mt-3 grid gap-2">
      <div className="ops-mini-segmented-control w-full">
        <button
          className="ops-mini-segmented-button"
          data-active={mode === "all"}
          disabled={disabled || updating}
          onClick={() => setMode("all")}
          type="button"
        >
          {t("All venues")}
        </button>
        <button
          className="ops-mini-segmented-button"
          data-active={mode === "specific"}
          disabled={disabled || updating}
          onClick={() => setMode("specific")}
          type="button"
        >
          {t("Specific")}
        </button>
      </div>

      {mode === "specific" ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {venues.length > 0 ? (
            venues.map((venue) => (
              <label className="ops-checkbox-row" key={venue.id}>
                <input
                  checked={selectedVenueIds.includes(venue.id)}
                  disabled={disabled || updating}
                  onChange={() => toggleVenue(venue.id)}
                  type="checkbox"
                />
                <span className="truncate text-sm text-slate-200">{venue.name}</span>
              </label>
            ))
          ) : (
            <div className="ops-empty sm:col-span-2 lg:col-span-3">{t("No venues yet.")}</div>
          )}
        </div>
      ) : null}

      <button
        className="ops-button justify-self-start"
        data-size="sm"
        data-variant="secondary"
        disabled={disabled || updating || !hasChanges}
        onClick={() => void onSave(nextVenueIds)}
        type="button"
      >
        {updating ? t("Saving...") : t("Save venue access")}
      </button>
    </div>
  );
}

export function ProviderTeamCard({ venues }: { venues: ProviderVenue[] }) {
  const { t, resolvedLanguage } = useAppPreferences();
  const { summary, user } = useProviderWorkspace();
  const { confirm, dialog } = useHardConfirm();
  const canManageTeam = resolveProviderMembershipRole(summary, user) === "owner";
  const {
    members,
    isLoading,
    error,
    notice,
    creating,
    revokingId,
    updatingVenueUserId,
    updatingRoleUserId,
    createMember,
    updateMemberVenues,
    updateMemberRole,
    revokeMember,
  } = useProviderTeam();
  const [form, setForm] = useState<CreateProviderStaffInput>(initialForm);
  const formVenueIds = useMemo(() => form.venue_ids ?? [], [form.venue_ids]);

  const updateField = <Field extends keyof CreateProviderStaffInput>(
    field: Field,
    value: CreateProviderStaffInput[Field],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const created = await createMember(form);
    if (created) {
      setForm(initialForm);
    }
  };

  const setFormScopeMode = (mode: VenueScopeMode) => {
    setForm((current) => ({
      ...current,
      venue_ids: mode === "all" ? null : [],
    }));
  };

  const toggleFormVenue = (venueId: string) => {
    setForm((current) => {
      const currentVenueIds = current.venue_ids ?? [];
      return {
        ...current,
        venue_ids: currentVenueIds.includes(venueId)
          ? currentVenueIds.filter((id) => id !== venueId)
          : [...currentVenueIds, venueId],
      };
    });
  };

  return (
    <div className="ops-surface">
      {dialog}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="ops-kicker">{t("Team access")}</p>
        <div className="ops-chip">
          {t(members.length === 1 ? "{count} member" : "{count} members", { count: members.length })}
        </div>
      </div>

      {notice && (
        <div className="ops-banner mt-3" data-tone="success">
          {notice}
        </div>
      )}

      {error && (
        <div className="ops-banner mt-3" data-tone="danger">
          {error}
        </div>
      )}

      <div className="mt-3 grid gap-2">
        {isLoading ? (
          <div className="ops-record-dense text-sm leading-7 text-slate-300">
            {t("Loading team access...")}
          </div>
        ) : members.length > 0 ? (
          members.map((member) => {
            const memberRole = getMemberMembershipRole(member);
            const canEditVenues = canManageTeam && member.role === "provider_staff" && member.is_active;
            const canRevoke = canManageTeam && member.role === "provider_staff";

            return (
              <div className="ops-record-dense" key={member.user_id}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">
                      {member.display_name ?? member.email}
                    </p>
                    <p className="truncate text-xs text-slate-400">{member.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canManageTeam && member.is_active ? (
                      <select
                        aria-label={t("Change role")}
                        className="ops-select"
                        disabled={updatingRoleUserId === member.user_id}
                        onChange={(event) => {
                          const nextRole = event.target.value as ProviderMembershipRole;
                          if (nextRole !== memberRole) {
                            void updateMemberRole(member.user_id, nextRole);
                          }
                        }}
                        value={memberRole}
                      >
                        <option value="owner">{t("Owner")}</option>
                        <option value="manager">{t("Manager")}</option>
                        <option value="front_desk">{t("Front desk")}</option>
                      </select>
                    ) : (
                      <span className="ops-chip">{membershipRoleLabel(memberRole, t)}</span>
                    )}
                    <span className="ops-chip" data-tone="info">
                      {getVenueScopeLabel(member, venues, t)}
                    </span>
                    {!member.is_active && (
                      <span className="ops-chip" data-tone="warning">
                        {t("Revoked")}
                      </span>
                    )}
                    {canRevoke && member.is_active && (
                      <button
                        className="ops-button"
                        data-size="sm"
                        data-variant="danger"
                        disabled={revokingId === member.user_id}
                        onClick={() => {
                          void (async () => {
                            const confirmationValue = member.email.trim() || "REVOKE";
                            const isConfirmed = await confirm({
                              confirmationValue,
                              confirmButtonLabel: t("Revoke access"),
                              confirmFieldLabel: t("Type {value} to revoke this staff account", { value: confirmationValue }),
                              consequence: resolvedLanguage === "ar"
                                ? "الحساب الملغي بيفقد الوصول. إعادة تفعيله تحتاج مسؤول المنصة؛ دعوة نفس البريد تاني مش هترجعه."
                                : "Revoked accounts lose access. A platform admin must reactivate the account; inviting the same email again will not restore it.",
                              description: t("This removes provider workspace access for {name}.", { name: member.display_name ?? member.email }),
                              title: t("Revoke access for {name}?", { name: member.display_name ?? member.email }),
                            });
                            if (!isConfirmed) {
                              return;
                            }

                            await revokeMember(member.user_id);
                          })();
                        }}
                        type="button"
                      >
                        {revokingId === member.user_id ? t("Revoking...") : t("Revoke")}
                      </button>
                    )}
                  </div>
                </div>
                {member.role === "provider_staff" ? (
                  <TeamVenueScopeEditor
                    disabled={!canEditVenues}
                    member={member}
                    onSave={(venueIds) => updateMemberVenues(member.user_id, venueIds)}
                    updating={updatingVenueUserId === member.user_id}
                    venues={venues}
                  />
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="ops-empty">
            {t("No staff accounts yet. Add the first provider staff member below.")}
          </div>
        )}
      </div>

      {canManageTeam ? (
        <form className="mt-4 grid gap-3 border-t border-white/10 pt-4" onSubmit={(event) => void handleSubmit(event)}>
          <p className="ops-copy text-sm">{resolvedLanguage === "ar"
            ? "لإضافة شريك مالك، أضفه كمدير وحدد الأماكن المتاحة له، وبعد الحفظ غيّر دوره إلى مالك من القائمة فوق. صلاحيات الأماكن بتفضل زي ما هي."
            : "For a co-owner, add a manager with the intended venue access, then change their saved role to Owner above. Their venue access stays unchanged."}</p>
          <div className="grid gap-3 lg:grid-cols-4">
            <label className="ops-field">
              <span className="ops-label">{t("Staff name")}</span>
              <input
                className="ops-input"
                onChange={(event) => updateField("display_name", event.target.value)}
                placeholder={t("Venue Supervisor")}
                required
                value={form.display_name}
              />
            </label>
            <label className="ops-field">
              <span className="ops-label">{t("Staff email")}</span>
              <input
                autoComplete="email"
                className="ops-input"
                onChange={(event) => updateField("email", event.target.value)}
                placeholder="staff@gaamed.com"
                required
                type="email"
                value={form.email}
              />
            </label>
            <label className="ops-field">
              <span className="ops-label">{t("Temporary password")}</span>
              <input
                autoComplete="new-password"
                className="ops-input"
                onChange={(event) => updateField("password", event.target.value)}
                placeholder="ChangeMe123!"
                required
                type="password"
                value={form.password}
              />
            </label>
            <label className="ops-field">
              <span className="ops-label">{t("Role")}</span>
              <select
                className="ops-select"
                onChange={(event) => updateField("role", event.target.value as "front_desk" | "manager")}
                value={form.role ?? "front_desk"}
              >
                <option value="front_desk">{t("Front desk")}</option>
                <option value="manager">{t("Manager")}</option>
              </select>
            </label>
          </div>
          <div className="grid gap-2">
            <div className="ops-mini-segmented-control w-full">
              <button
                className="ops-mini-segmented-button"
                data-active={form.venue_ids === null}
                onClick={() => setFormScopeMode("all")}
                type="button"
              >
                {t("All venues")}
              </button>
              <button
                className="ops-mini-segmented-button"
                data-active={form.venue_ids !== null}
                onClick={() => setFormScopeMode("specific")}
                type="button"
              >
                {t("Specific")}
              </button>
            </div>
            {form.venue_ids !== null ? (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {venues.map((venue) => (
                  <label className="ops-checkbox-row" key={venue.id}>
                    <input
                      checked={formVenueIds.includes(venue.id)}
                      onChange={() => toggleFormVenue(venue.id)}
                      type="checkbox"
                    />
                    <span className="truncate text-sm text-slate-200">{venue.name}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <button
            className="ops-button justify-self-start"
            data-size="sm"
            data-variant="primary"
            disabled={creating}
            type="submit"
          >
            {creating ? t("Adding staff...") : t("Add staff member")}
          </button>
        </form>
      ) : (
        <div className="ops-record-dense mt-4 text-sm leading-7 text-slate-300">
          {t("Only provider owners can add or revoke staff access. Provider staff can still view the current team roster.")}
        </div>
      )}
    </div>
  );
}
